export type MessageStatus = "sent" | "delivered" | "read" | "failed" | "received";
export type TemplateStatus = "approved" | "pending" | "rejected" | "draft";
export type BroadcastStatus = "draft" | "scheduled" | "sending" | "completed" | "failed";
export type ChannelQuality = "high" | "medium" | "low";

export const metrics = {
  messagesToday: 12847,
  deliveryRate: 98.4,
  activeConversations: 342,
  balance: 1284.5,
};

export const channels = [
  { id: "ch_1", phone: "+1 415 555 0142", name: "InferenceLabs Main", waba: "WABA-8821", quality: "high" as ChannelQuality, tier: "100K", status: "Active", usage: 12847, limit: 100000 },
  { id: "ch_2", phone: "+44 20 7946 0814", name: "EU Support", waba: "WABA-3290", quality: "high" as ChannelQuality, tier: "10K", status: "Active", usage: 2104, limit: 10000 },
  { id: "ch_3", phone: "+91 80 4567 1230", name: "APAC Sales", waba: "WABA-1145", quality: "medium" as ChannelQuality, tier: "10K", status: "Active", usage: 8930, limit: 10000 },
  { id: "ch_4", phone: "+55 11 9876 5432", name: "LATAM Promo", waba: "WABA-7702", quality: "low" as ChannelQuality, tier: "1K", status: "Limited", usage: 980, limit: 1000 },
];

const sampleContent = [
  "Your order #4821 has shipped. Track it at...",
  "Hi! Thanks for reaching out. How can we help?",
  "Your OTP is 482910. Valid for 5 minutes.",
  "Flash sale ends tonight! Get 30% off everything.",
  "Appointment confirmed for tomorrow at 3:00 PM.",
  "We've received your payment of $128.50. Thank you!",
];

export const messages = Array.from({ length: 32 }, (_, i) => ({
  id: `msg_${1000 + i}`,
  timestamp: new Date(Date.now() - i * 1000 * 60 * 7).toISOString(),
  status: (["read", "delivered", "sent", "failed", "received", "read", "delivered"][i % 7]) as MessageStatus,
  recipient: `+1 415 555 0${100 + i}`,
  channel: channels[i % channels.length].name,
  direction: (i % 4 === 0 ? "IN" : "OUT") as "IN" | "OUT",
  source: (["API", "Broadcast", "Manual"][i % 3]) as "API" | "Broadcast" | "Manual",
  content: sampleContent[i % sampleContent.length],
}));

export const templates = [
  { id: "tpl_001", name: "order_shipped", language: "en_US", category: "Utility", status: "approved" as TemplateStatus, channel: "InferenceLabs Main", modified: "2 days ago" },
  { id: "tpl_002", name: "otp_verification", language: "en_US", category: "Authentication", status: "approved" as TemplateStatus, channel: "InferenceLabs Main", modified: "1 week ago" },
  { id: "tpl_003", name: "flash_sale_2026", language: "en_US", category: "Marketing", status: "pending" as TemplateStatus, channel: "InferenceLabs Main", modified: "3 hours ago" },
  { id: "tpl_004", name: "appointment_reminder", language: "en_GB", category: "Utility", status: "approved" as TemplateStatus, channel: "EU Support", modified: "5 days ago" },
  { id: "tpl_005", name: "promo_diwali", language: "hi_IN", category: "Marketing", status: "rejected" as TemplateStatus, channel: "APAC Sales", modified: "1 day ago" },
  { id: "tpl_006", name: "welcome_series_1", language: "en_US", category: "Marketing", status: "draft" as TemplateStatus, channel: "InferenceLabs Main", modified: "Just now" },
  { id: "tpl_007", name: "payment_received", language: "pt_BR", category: "Utility", status: "approved" as TemplateStatus, channel: "LATAM Promo", modified: "2 weeks ago" },
];

export const broadcasts = [
  { id: "bc_001", name: "Black Friday Preview", status: "completed" as BroadcastStatus, channel: "InferenceLabs Main", template: "flash_sale_2026", recipients: 12450, sent: 12450, delivered: 12201, read: 8934, failed: 249, scheduled: "Nov 22, 2026 09:00" },
  { id: "bc_002", name: "December Newsletter", status: "scheduled" as BroadcastStatus, channel: "EU Support", template: "welcome_series_1", recipients: 8200, sent: 0, delivered: 0, read: 0, failed: 0, scheduled: "Dec 01, 2026 10:00" },
  { id: "bc_003", name: "APAC Loyalty Push", status: "sending" as BroadcastStatus, channel: "APAC Sales", template: "promo_diwali", recipients: 4300, sent: 2840, delivered: 2710, read: 1822, failed: 130, scheduled: "Now" },
  { id: "bc_004", name: "LATAM Reactivation", status: "draft" as BroadcastStatus, channel: "LATAM Promo", template: "welcome_series_1", recipients: 0, sent: 0, delivered: 0, read: 0, failed: 0, scheduled: "—" },
  { id: "bc_005", name: "Q3 Survey Invite", status: "failed" as BroadcastStatus, channel: "InferenceLabs Main", template: "order_shipped", recipients: 1200, sent: 412, delivered: 380, read: 110, failed: 832, scheduled: "Oct 15, 2026" },
];

export const contacts = Array.from({ length: 14 }, (_, i) => ({
  id: `ct_${i}`,
  name: ["Ava Chen", "Marcus Lee", "Priya Patel", "João Silva", "Sofia Rossi", "Liam O'Connor", "Hana Yamada", "Noah Williams", "Elena Petrova", "Omar Hassan", "Mei Lin", "Lucas Martin", "Zara Khan", "Diego Cruz"][i],
  phone: `+1 415 555 0${200 + i}`,
  tags: [["vip", "newsletter"], ["lead"], ["customer", "vip"], ["lead", "cold"], ["customer"], ["newsletter"], ["customer", "newsletter"], ["lead"], ["vip"], ["customer"], ["lead"], ["customer", "newsletter"], ["vip", "lead"], ["customer"]][i],
  optIn: i % 5 !== 0,
  lastMessage: `${i + 1}d ago`,
}));

export const cascades = [
  { id: "csc_1", name: "Order confirmations", status: "active", channels: ["WhatsApp", "SMS", "Email"], timeout: "10 min" },
  { id: "csc_2", name: "OTP fallback", status: "active", channels: ["WhatsApp", "SMS"], timeout: "2 min" },
  { id: "csc_3", name: "Promo blast", status: "inactive", channels: ["WhatsApp", "Email"], timeout: "30 min" },
];

export const tenants = [
  { id: "t_1", company: "Acme Retail Co.", status: "Active", plan: "Growth", balance: 4820.0, channels: 4, lastActivity: "2 min ago" },
  { id: "t_2", company: "Bluefin Logistics", status: "Active", plan: "Scale", balance: 12480.5, channels: 8, lastActivity: "11 min ago" },
  { id: "t_3", company: "Northwind Health", status: "Trial", plan: "Trial", balance: 100.0, channels: 1, lastActivity: "1 hr ago" },
  { id: "t_4", company: "Sunset Cosmetics", status: "Suspended", plan: "Starter", balance: 0, channels: 2, lastActivity: "4 days ago" },
  { id: "t_5", company: "Volta Energy", status: "Active", plan: "Growth", balance: 2200.0, channels: 3, lastActivity: "32 min ago" },
];
